'use client'

import Link from 'next/link'
import { useRegion } from '@/contexts/marketing-region-context'
import { PLANS } from '@/lib/billing/plans'
import {
  getLocalizedPlanPrices,
  PRICING_REGIONS,
  REGION_CONFIGS,
  type PricingRegion,
} from '@/lib/billing/region'

const PRICING_STORAGE_KEY = 'bb_pricing_region'

function flatFeatures(plan: (typeof PLANS)[number]): string[] {
  return plan.featureGroups.flatMap((g) => g.items)
}

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="shrink-0 mt-0.5">
      <path d="M2 7l3 3 7-7" stroke="#059669" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export function MarketingPricingSection() {
  const { region, setRegion, config } = useRegion()
  const prices = getLocalizedPlanPrices(region)

  function selectRegion(nextRegion: PricingRegion) {
    setRegion(nextRegion)
    try { localStorage.setItem(PRICING_STORAGE_KEY, nextRegion) } catch { /* ignore */ }
  }

  return (
    <section id="pricing" className="border-t border-slate-100 bg-white py-11 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">

        <div className="mx-auto mb-8 max-w-2xl text-left sm:mb-10 sm:text-center">
          <p className="text-xs uppercase tracking-[0.18em] text-[#c2571a] font-semibold mb-3">Pricing</p>
          <h2 className="text-[1.95rem] font-bold leading-[1.08] tracking-tight text-slate-900 sm:text-[2.8rem] sm:leading-[1.06]">
            Simple, honest pricing
          </h2>
          <p className="mt-3 text-[1rem] leading-relaxed text-slate-600 sm:mt-4 sm:text-[1.05rem] sm:text-slate-500">
            Start free. No card required. Upgrade when you&apos;re ready.
          </p>
        </div>

        {/* Region selector */}
        <div className="mb-8 rounded-2xl border border-orange-200 bg-orange-50/70 p-4 shadow-sm sm:mb-10">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.16em] text-[#c2571a]">Country / currency</p>
              <p className="mt-1 text-sm text-slate-700">
                Showing {config.currencyCode} prices for {config.label}. Change country to see local pricing.
              </p>
            </div>
            <label className="sr-only" htmlFor="landing-pricing-region">Country / currency</label>
            <select
              id="landing-pricing-region"
              value={region}
              onChange={(event) => selectRegion(event.target.value as PricingRegion)}
              className="min-h-12 w-full rounded-xl border border-orange-200 bg-white px-3 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c2571a]/20 sm:w-64"
            >
              {PRICING_REGIONS.map((r) => (
                <option key={r} value={r}>
                  {REGION_CONFIGS[r].label} - {REGION_CONFIGS[r].currencyCode}
                </option>
              ))}
            </select>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {PRICING_REGIONS.map((r) => (
              <button
                key={r}
                type="button"
                onClick={() => selectRegion(r)}
                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${
                  region === r
                    ? 'border-[#c2571a] bg-white text-[#c2571a] shadow-sm'
                    : 'border-orange-200 bg-white/70 text-slate-600 hover:border-[#c2571a]/50'
                }`}
              >
                <span aria-hidden>{REGION_CONFIGS[r].flag}</span> {REGION_CONFIGS[r].currencyCode}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8 flex flex-col gap-4 rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 shadow-sm sm:mb-10 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-bold text-slate-900">Not ready to pick a plan?</p>
            <p className="mt-1 text-sm text-slate-600">Create the free account now. No card, no checkout, upgrade later.</p>
          </div>
          <Link
            href={`/signup?region=${region}`}
            className="inline-flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-slate-800 sm:w-auto"
          >
            Create free account
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
              <path d="M2.5 7h9M8.5 4l3 3-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Link>
        </div>

        {/* Plan cards */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {PLANS.map((plan) => {
            const monthlyPrice = prices[plan.id as keyof typeof prices]
            const priceDisplay = monthlyPrice === 0
              ? `${config.currencySymbol}0`
              : `${config.currencySymbol}${monthlyPrice}`
            const checkoutHref = plan.id === 'free'
              ? `/signup?region=${region}`
              : `/signup?plan=${plan.id}&region=${region}`
            const features = flatFeatures(plan)

            return (
              <div
                key={plan.id}
                className={`relative flex flex-col rounded-2xl border p-6 ${
                  plan.mostPopular
                    ? 'border-[#c2571a] shadow-[0_0_0_1px_rgba(194,87,26,0.15),0_8px_32px_rgba(194,87,26,0.12)] bg-orange-50/30'
                    : 'border-slate-200 bg-white shadow-sm'
                }`}
              >
                {plan.mostPopular && (
                  <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap">
                    <span className="inline-flex items-center rounded-full bg-[#c2571a] px-3.5 py-1 text-[11px] font-bold text-white uppercase tracking-[0.1em] shadow-sm">
                      Most popular
                    </span>
                  </div>
                )}

                <div className="mb-4">
                  <h3 className="text-[17px] font-bold text-slate-900 mb-1">{plan.name}</h3>
                  <p className="text-[13px] text-slate-500">{plan.tagline}</p>
                </div>

                <div className="mb-5">
                  <div className="flex items-end gap-1">
                    <span className="text-[2.4rem] font-extrabold text-slate-900 leading-none tracking-tight">
                      {priceDisplay}
                    </span>
                    {monthlyPrice > 0 && (
                      <span className="text-[14px] text-slate-500 mb-1 font-medium">/month</span>
                    )}
                  </div>
                  <p className="text-[12px] text-slate-400 mt-1">
                    {monthlyPrice === 0 ? 'free forever' : 'Billed monthly · Cancel anytime'}
                  </p>
                </div>

                <Link
                  href={checkoutHref}
                  className={`mb-5 flex min-h-12 items-center justify-center rounded-xl px-5 py-3 text-[14px] font-semibold transition-all ${
                    plan.mostPopular
                      ? 'bg-[#c2571a] text-white hover:bg-[#a84915] shadow-sm hover:-translate-y-0.5'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {plan.id === 'free' ? 'Create free account' : `Choose ${plan.name}`}
                </Link>

                <div className="space-y-2.5 flex-1">
                  {features.map((f) => (
                    <div key={f} className="flex items-start gap-2.5">
                      <CheckIcon />
                      <span className="text-[13.5px] text-slate-700">{f}</span>
                    </div>
                  ))}
                </div>
              </div>
            )
          })}
        </div>

        <div className="mt-8 flex flex-col items-center gap-3">
          <Link
            href={`/pricing?region=${region}`}
            className="inline-flex items-center gap-1 text-sm font-semibold text-[#c2571a] hover:underline"
          >
            View full pricing details &amp; annual billing options
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2 6h8M6.5 3l3 3-3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </Link>
          <p className="text-center text-[13px] text-slate-400">
            All plans include a free trial · No card required · Cancel any time
          </p>
        </div>

      </div>
    </section>
  )
}
